/**
 * Shared helpers for run status, channel, and trigger display.
 *
 * Used by TaskRunRow and NavHistory to ensure consistent run metadata
 * rendering (status dots, labels, channel icons, trigger badges).
 */

import { Code, Globe, Mail, MessageSquare, Terminal, Webhook } from "lucide-react";
import type { components } from "@/lib/api/types";

type RunResponse = components["schemas"]["RunResponse"];

/** Channel icon map — shared between NavHistory and TaskBoard. */
export const channelIcons: Record<string, typeof Globe> = {
	app: Globe,
	cli: Terminal,
	email: Mail,
	slack: MessageSquare,
	webhook: Webhook,
	api: Code,
};

/** Staleness threshold: runs older than 6h are considered stalled. */
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

export const isActivelyRunning = (run: RunResponse) =>
	(run.status === "running" || run.status === "in_progress") &&
	!!run.created_at &&
	Date.now() - new Date(run.created_at).getTime() < SIX_HOURS_MS;

export const isAwaitingInput = (run: RunResponse) =>
	run.status === "awaiting_approval" &&
	!!run.created_at &&
	Date.now() - new Date(run.created_at).getTime() < SIX_HOURS_MS;

export const isStalled = (run: RunResponse) =>
	(run.status === "running" ||
		run.status === "in_progress" ||
		run.status === "awaiting_approval") &&
	!isActivelyRunning(run) &&
	!isAwaitingInput(run);

/** Completed successfully but user hasn't seen the latest activity.
 *  Excludes refusal/max_tokens — those always show their amber warning state. */
export const isUnseen = (run: RunResponse) =>
	(run.status === "completed" || run.status === "success") &&
	run.stop_reason !== "refusal" &&
	run.stop_reason !== "max_tokens" &&
	(!run.last_viewed_at ||
		!run.last_activity_at ||
		new Date(run.last_viewed_at) < new Date(run.last_activity_at));

/** Status label matching NavHistory's logic. */
export const getStatusLabel = (run: RunResponse): string => {
	if (isAwaitingInput(run)) return "Needs action";
	if (isActivelyRunning(run)) return "Task in progress";
	if (isStalled(run)) return "Stalled";
	if (isUnseen(run)) return "New";
	if (run.status === "completed" || run.status === "success") {
		if (run.stop_reason === "refusal") return "Refused";
		if (run.stop_reason === "max_tokens") return "Truncated";
		return "Completed";
	}
	if (run.status === "failed" || run.status === "error") return "Failed";
	if (run.status === "cancelled") return "Cancelled";
	return run.status ?? "Unknown";
};

/** Status dot color class. Returns null for spinner states (actively running). */
export const getStatusColor = (run: RunResponse): string | null => {
	if (isActivelyRunning(run)) return null; // spinner
	if (isAwaitingInput(run)) return "bg-amber-500 animate-pulse";
	if (isStalled(run)) return "bg-red-600";
	if (isUnseen(run)) return "bg-blue-500";
	if (run.status === "completed" || run.status === "success") {
		if (run.stop_reason === "refusal" || run.stop_reason === "max_tokens") return "bg-amber-500";
		return "bg-green-600";
	}
	if (run.status === "failed" || run.status === "error") return "bg-red-600";
	return "bg-muted-foreground/50";
};

/** Channel tooltip — explains how the run was started. */
export const channelLabel: Record<string, string> = {
	app: "Started via web app",
	cli: "Started via CLI",
	email: "Started via email",
	slack: "Started via Slack",
	webhook: "Started via webhook",
	api: "Started via API",
};
